import * as React from 'react';
import { focusNextFillableControl } from '@/lib/focusNextFillableControl';
import {
  FormProvider,
  useFormContext as rhfUseFormContext,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

interface FormProps<TValues extends FieldValues> extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'children'
> {
  form: UseFormReturn<TValues>;
  onSubmit: SubmitHandler<TValues>;
  children: React.ReactNode;
  /**
   * Enter steps to the next field the user can fill in, instead of submitting.
   *
   * For the long item forms — a cabinet, a panel, a countertop — which are filled
   * in as a run and where Enter meaning "save" fires a create from a form the
   * user is still halfway through. Off by default: on a short form (login, a
   * search box) Enter-to-submit is exactly right.
   */
  advanceOnEnter?: boolean;
}

/**
 * Thin wrapper around `<form>` + RHF's `FormProvider`.
 *
 * - Wires `form.handleSubmit(onSubmit)` to the form element.
 * - Adds `noValidate` so we use Zod-driven errors instead of native browser tooltips.
 * - Establishes the RHF context for the field primitives below.
 */
export function Form<TValues extends FieldValues>({
  form,
  onSubmit,
  children,
  advanceOnEnter,
  onKeyDown,
  ...rest
}: FormProps<TValues>) {
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!advanceOnEnter || event.key !== 'Enter') return;
          // A field that has already dealt with Enter itself — an autosuggest
          // taking the highlighted option, say — has called preventDefault, and
          // moving focus out from under it would undo the choice.
          if (event.defaultPrevented) return;
          const target = event.target;
          // A textarea wants a newline, and a focused button wants activating.
          if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
          event.preventDefault();
          focusNextFillableControl(target);
        }}
        {...rest}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export const useFormContext = rhfUseFormContext;
